import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';

import { AssetColumnId, AssetDTO, GetAssetsRequestDTO, MODULE, PdfScope, RecordState } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RowSelectionState } from '@tanstack/react-table';
import { useDebounce } from '@uidotdev/usehooks';

import AssetDrawer from '@/components/assets/asset-drawer';
import BulkAssetImportDialog from '@/components/assets/bulk-asset-import-dialog';
import { QRBlankDialog } from '@/components/assets/qr-blank-dialog';
import { QRDialog } from '@/components/assets/qr-dialog';
import { QRPrintDocument } from '@/components/assets/qr-print-document';
import { ArchiveConfirmationDialog } from '@/components/common/archive-confirmation-dialog';
import { RestoreConfirmationDialog } from '@/components/common/restore-confirmation-dialog';
import ArchiveIcon from '@/components/icons/archive';
import { ColumnsIcon } from '@/components/icons/columns';
import DeleteIcon from '@/components/icons/delete';
import DownloadIcon from '@/components/icons/download';
import FolderOpenIcon from '@/components/icons/folder-open';
import GearIcon from '@/components/icons/gear';
import MapIcon from '@/components/icons/map';
import PlusIcon from '@/components/icons/plus';
import QrCodeIcon from '@/components/icons/qr-code';
import TagIcon from '@/components/icons/tag';
import UndoIcon from '@/components/icons/undo';
import ColumnsDialog from '@/components/table-page/columns-dialog';
import { DownloadPdfMenu } from '@/components/table-page/download-pdf-menu';
import { TablePageHeader, TablePageHeaderActionButton, TablePageTableGroupDropdown } from '@/components/table-page/table-page-header';
import TablePageSearchBar from '@/components/table-page/table-page-search-bar';
import { TablePageSelectedCountIndicator } from '@/components/table-page/table-page-selected-count-indicator';
import { TablePageTitle } from '@/components/table-page/table-page-title';
import { useTopbarStore } from '@/components/topbar/topbar-store';
import { ButtonStateToggle } from '@/components/ui/button-state-toggle';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { useToast } from '@/components/ui/toaster';
import { useInfiniteScrollTrigger } from '@/hooks/use-infinite-scroll-trigger';
import { usePageConfig } from '@/hooks/use-page-config';
import { usePermissions } from '@/hooks/use-permissions';
import { useResetRowSelectionOnChange } from '@/hooks/use-reset-row-selection-on-change';
import { useAssetQRCodePrint } from '@/hooks/useAssetQRCodePrint';
import { assetKeys, getAssetsInfiniteQueryOptions, qrCodeKeys, tagKeys } from '@/queries/assets';
import { buildFilterFields } from '@/queries/column-filter';
import { getUnitsQueryOptions } from '@/queries/units';
import { ApiService } from '@/services/api-v2';
import { useModulesStore } from '@/stores/modules.store';
import { openExportTab } from '@/utils/export-tab';
import { buildCsvExportFilename } from '@/utils/export-to-csv';
import { activeExportColumnIds, buildPdfExportJob, RegisterExportQuery } from '@/utils/register-pdf-export';

import { DeleteConfirmationDialog } from '../../components/common/delete-confirmation-dialog';
import { BulkUpdateAssetCategoryDialog } from './bulk-update-asset-category-dialog';
import { BulkUpdateAssetLocationDialog } from './bulk-update-asset-location-dialog';
import { BulkUpdateAssetTagsDialog } from './bulk-update-asset-tags-dialog';
import { AssetsTable, NoAssetsCreatedDisplay, NoAssetsFoundFromFiltersDisplay, NoAssetsFoundFromSearchDisplay } from './table';
import { createAssetTableColumns, FILTER_FIELD_MAP, groupOptions } from './table-meta';
import { TopbarActions } from './topbar-actions';

function AssetsPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { setModuleFilters } = useModulesStore();
  const { canManageAssets, canExportCsv } = usePermissions();

  const { data: units, isLoading: isLoadingUnits } = useQuery(getUnitsQueryOptions());

  const {
    config: { columnOrder, grouping, sorting, filters, columnVisibility },
    loading: isLoadingPageConfig,
    updatePageConfig,
  } = usePageConfig(MODULE.Assets, {
    filterFieldMap: FILTER_FIELD_MAP,
    groupOptions,
    columns: createAssetTableColumns({ units: units }),
  });

  const [openAssetDrawer, setOpenAssetDrawer] = useState(false);
  const [openBulkImport, setOpenBulkImport] = useState(false);
  const [openColumnsDialog, setOpenColumnsDialog] = useState(false);
  const [openArchiveConfirmation, setOpenArchiveConfirmation] = useState(false);
  const [openRestoreConfirmation, setOpenRestoreConfirmation] = useState(false);
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
  const [openBulkUpdateAssetCategoryDialog, setOpenBulkUpdateAssetCategoryDialog] = useState(false);
  const [openBulkUpdateAssetLocationDialog, setOpenBulkUpdateAssetLocationDialog] = useState(false);
  const [openBulkUpdateAssetTagsDialog, setOpenBulkUpdateAssetTagsDialog] = useState(false);
  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});
  const [assetState, setAssetState] = useState<RecordState>(RecordState.Active);
  const [openQRDialog, setQRDialogOpen] = useState(false);
  const [openQRBlankDialog, setQRBlankDialogOpen] = useState(false);
  const [search, setSearch] = useState('');

  const debouncedSearch = useDebounce(search, 500);

  useLayoutEffect(() => {
    useTopbarStore.getState().setActions(<TopbarActions />);

    return () => {
      useTopbarStore.getState().setActions(<></>);
    };
  }, []);

  const {
    data: assetsData,
    fetchNextPage: fetchNextAssetsPage,
    hasNextPage: hasNextAssetsPage,
    isFetching: isFetchingAssets,
    isFetchingNextPage: isFetchingNextAssetsPage,
    error: assetsError,
  } = useInfiniteQuery(
    getAssetsInfiniteQueryOptions({
      filters: {
        state: assetState,
        search: debouncedSearch,
        ...buildFilterFields(filters, FILTER_FIELD_MAP),
      },
      groupBy: grouping.length
        ? { field: grouping[0] as NonNullable<GetAssetsRequestDTO['groupBy']>['field'], direction: (sorting.direction || 'asc') as 'asc' | 'desc' }
        : undefined,
      orderBy: sorting.key
        ? { field: sorting.key as NonNullable<GetAssetsRequestDTO['orderBy']>['field'], direction: sorting.direction as 'asc' | 'desc' }
        : undefined,
      enabled: !isLoadingPageConfig,
    }),
  );

  const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null);
  const { sentinelRef, canLoadMore, loadMore } = useInfiniteScrollTrigger({
    fetchNextPage: fetchNextAssetsPage,
    hasNextPage: hasNextAssetsPage,
    isFetching: isFetchingAssets,
    isError: !!assetsError,
    root: scrollRoot,
  });

  const getSelectedAssets = useMemo(() => {
    return (incomingData: typeof assetsData) => {
      if (!incomingData?.pages) return [];
      const ids = Object.keys(selectedRows);
      const assets = incomingData.pages.flatMap((page) => page.data);
      return ids
        .filter((id) => !isNaN(parseInt(id)))
        .map((id) => assets[parseInt(id)])
        .filter((asset): asset is (typeof assets)[number] => asset != null);
    };
  }, [selectedRows]);

  const { mutateAsync: bulkUpdateAssetState, isPending: isUpdatingAssetState } = useMutation({
    mutationFn: async ({ ids, state }: { ids: string[]; state: RecordState }) => await ApiService.BulkUpdateAssetState({ ids, state }),
    onSuccess: (_, { state }) => {
      const messages = {
        [RecordState.Active]: 'Assets restored successfully',
        [RecordState.Archived]: 'Assets archived successfully',
        [RecordState.Deleted]: 'Assets deleted successfully',
      };
      toast.success(messages[state]);
      setOpenArchiveConfirmation(false);
      setOpenRestoreConfirmation(false);
      setOpenDeleteConfirmation(false);
      setSelectedRows({});

      queryClient.invalidateQueries({ queryKey: assetKeys.all });
      queryClient.invalidateQueries({ queryKey: qrCodeKeys.all });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  function handleArchiveAssets() {
    if (assetsData?.pages) {
      const toArchive = getSelectedAssets(assetsData);
      bulkUpdateAssetState({ ids: toArchive.map((asset) => asset.id), state: RecordState.Archived });
    }
  }

  function handleRestoreAssets() {
    if (assetsData?.pages) {
      const toRestore = getSelectedAssets(assetsData);
      bulkUpdateAssetState({ ids: toRestore.map((asset) => asset.id), state: RecordState.Active });
    }
  }

  function handleDeleteAssets() {
    if (assetsData?.pages) {
      const toDelete = getSelectedAssets(assetsData);
      bulkUpdateAssetState({ ids: toDelete.map((asset) => asset.id), state: RecordState.Deleted });
    }
  }

  const { mutateAsync: bulkUpdateAssetCategory, isPending: isUpdatingAssetCategory } = useMutation({
    mutationFn: async ({ ids, categoryId }: { ids: string[]; categoryId: string }) => await ApiService.UpdateAssets({ assetIds: ids, asset: { categoryId } }),
    onSuccess: () => {
      toast.success('Asset category updated successfully');
      setOpenBulkUpdateAssetCategoryDialog(false);
      setSelectedRows({});
      queryClient.invalidateQueries({
        queryKey: assetKeys.all,
      });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  async function handleBulkUpdateAssetCategory(categoryId: string, resetForm: () => void) {
    if (assetsData?.pages) {
      const toUpdate = getSelectedAssets(assetsData);
      await bulkUpdateAssetCategory({ ids: toUpdate.map((asset) => asset.id), categoryId });
      resetForm();
    }
  }

  const { mutateAsync: bulkUpdateAssetLocation, isPending: isUpdatingAssetLocation } = useMutation({
    mutationFn: async ({ ids, locationId }: { ids: string[]; locationId: string }) =>
      await ApiService.UpdateAssets({ assetIds: ids, asset: { locationId: locationId } }),
    onSuccess: () => {
      toast.success('Asset location updated successfully');
      setOpenBulkUpdateAssetLocationDialog(false);
      setSelectedRows({});
      queryClient.invalidateQueries({
        queryKey: assetKeys.all,
      });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  async function handleBulkUpdateAssetLocation(locationId: string, resetForm: () => void) {
    if (assetsData?.pages) {
      const toUpdate = getSelectedAssets(assetsData);
      await bulkUpdateAssetLocation({ ids: toUpdate.map((asset) => asset.id), locationId });
      resetForm();
    }
  }

  const { mutateAsync: bulkUpdateAssetTags, isPending: isUpdatingAssetTags } = useMutation({
    mutationFn: async ({ ids, tags }: { ids: string[]; tags: string[] }) => await ApiService.UpdateAssets({ assetIds: ids, asset: { tags } }),
    onSuccess: () => {
      toast.success('Asset tags updated successfully');
      setOpenBulkUpdateAssetTagsDialog(false);
      setSelectedRows({});
      queryClient.invalidateQueries({
        queryKey: assetKeys.all,
      });

      queryClient.invalidateQueries({
        queryKey: tagKeys.all,
      });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  async function handleBulkUpdateAssetTags(tags: string[], resetForm: () => void) {
    if (assetsData?.pages) {
      const toUpdate = getSelectedAssets(assetsData);
      await bulkUpdateAssetTags({ ids: toUpdate.map((asset) => asset.id), tags });
      resetForm();
    }
  }

  const selectedAssets = useMemo(() => getSelectedAssets(assetsData), [assetsData, getSelectedAssets]);

  const { handlePrint: handlePrintAssetQrCodes, printQrRef, assetQRCodes: selectedAssetsQRCodes } = useAssetQRCodePrint(selectedAssets);

  const buildAssetsExportRequest = useCallback(
    (): RegisterExportQuery<'assets'> => ({
      queryType: 'assets',
      filters: { fields: { id: selectedAssets.map((asset) => asset.id) } },
      groupBy: grouping.length
        ? { field: grouping[0] as NonNullable<GetAssetsRequestDTO['groupBy']>['field'], direction: (sorting.direction || 'asc') as 'asc' | 'desc' }
        : undefined,
      orderBy: sorting.key
        ? { field: sorting.key as NonNullable<GetAssetsRequestDTO['orderBy']>['field'], direction: sorting.direction as 'asc' | 'desc' }
        : undefined,
      columns: activeExportColumnIds(columnOrder),
    }),
    [selectedAssets, grouping, sorting, columnOrder],
  );

  const handleDownload = useCallback(() => {
    openExportTab({
      kind: 'csv',
      request: { ...buildAssetsExportRequest(), filename: buildCsvExportFilename('assets') },
    });
  }, [buildAssetsExportRequest]);

  function handlePdfScopeSelect(scope: PdfScope) {
    if (!assetsData?.pages) return;
    openExportTab(buildPdfExportJob({ scope, records: [], register: buildAssetsExportRequest() }));
  }

  useEffect(() => {
    if (assetsError) {
      toast.error(assetsError.message);
    }
  }, [toast, assetsError]);

  useResetRowSelectionOnChange(setSelectedRows, [assetState, debouncedSearch, filters, sorting.key, sorting.direction, grouping]);

  const flattenedData = useMemo(() => assetsData?.pages.flatMap((page) => page.data) ?? [], [assetsData]);

  const handleSetQRBlankDialogOpen = () => {
    setQRBlankDialogOpen(true);
    setQRDialogOpen(false);
  };

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <TablePageHeader>
        <div className="no-scrollbar h-full w-full overflow-x-auto py-0.5">
          <div className="flex h-full min-w-[580px] items-center justify-between">
            {!selectedAssets.length && (
              <>
                <div className="flex h-full items-center gap-x-2 px-2">
                  <TablePageTitle>Assets</TablePageTitle>
                  <ButtonStateToggle
                    options={[
                      { icon: <FolderOpenIcon />, label: 'Active', value: RecordState.Active },
                      { icon: <ArchiveIcon />, label: 'Archived', value: RecordState.Archived },
                    ]}
                    value={assetState}
                    onChange={setAssetState}
                    className="font-medium"
                    responsive
                  />
                  <TablePageSearchBar search={search} setSearch={setSearch} />
                  {filters && filters.length > 0 && (
                    <TablePageHeaderActionButton
                      icon={<UndoIcon />}
                      label="Remove all Filters"
                      kind="secondary"
                      onClick={() => setModuleFilters(MODULE.Assets, [])}
                    />
                  )}
                </div>

                <div className="mr-1 flex h-full items-center">
                  <div className="flex h-full items-center">
                    <TablePageTableGroupDropdown
                      columns={groupOptions}
                      value={grouping.length ? grouping[0] : ''}
                      onChange={(value) =>
                        updatePageConfig({
                          table: { grouping: value ? [value] : [] },
                        })
                      }
                    />
                    <TablePageHeaderActionButton
                      icon={<ColumnsIcon />}
                      label="Edit Columns"
                      disabled={isLoadingPageConfig || isLoadingUnits}
                      onClick={() => setOpenColumnsDialog(true)}
                    />
                    {assetState === RecordState.Active && (
                      <QRDialog
                        open={openQRDialog}
                        setOpen={setQRDialogOpen}
                        setQRBlankDialogOpen={() => handleSetQRBlankDialogOpen()}
                        assetState={assetState}
                      />
                    )}
                    <QRBlankDialog open={openQRBlankDialog} onClose={() => setQRBlankDialogOpen(false)} />
                    {canManageAssets && <BulkAssetImportDialog open={openBulkImport} onOpenChange={setOpenBulkImport} />}
                  </div>
                  {canManageAssets && (
                    <TablePageHeaderActionButton
                      variant="success"
                      icon={<PlusIcon />}
                      kind="primary"
                      onClick={() => setOpenAssetDrawer(true)}
                      label="Create Asset"
                    />
                  )}
                  <AssetDrawer noTrigger open={openAssetDrawer} onOpenChange={setOpenAssetDrawer} onOpenBulkImport={() => setOpenBulkImport(true)} />
                </div>
              </>
            )}

            {selectedAssets.length > 0 && (
              <div className="flex items-center gap-1 px-2">
                <TablePageSelectedCountIndicator selectedCount={selectedAssets.length} totalCount={assetsData?.pages[0]?.totalCount ?? 0} />
                {canManageAssets && (
                  <>
                    <TablePageHeaderActionButton icon={<GearIcon />} label="Edit Asset Category" onClick={() => setOpenBulkUpdateAssetCategoryDialog(true)} />
                    <TablePageHeaderActionButton icon={<MapIcon />} label="Edit Asset Location" onClick={() => setOpenBulkUpdateAssetLocationDialog(true)} />
                    <TablePageHeaderActionButton icon={<TagIcon />} label="Edit Asset Tags" onClick={() => setOpenBulkUpdateAssetTagsDialog(true)} />
                  </>
                )}
                {assetState === RecordState.Active && (
                  <TablePageHeaderActionButton icon={<QrCodeIcon />} label="Generate QR Codes" onClick={handlePrintAssetQrCodes} />
                )}
                {canExportCsv && <TablePageHeaderActionButton icon={<DownloadIcon />} label="Download" onClick={handleDownload} />}
                {canExportCsv && <DownloadPdfMenu onSelect={handlePdfScopeSelect} />}
                {canManageAssets && assetState === RecordState.Active && (
                  <TablePageHeaderActionButton icon={<ArchiveIcon />} label="Archive Selected" onClick={() => setOpenArchiveConfirmation(true)} />
                )}
                {canManageAssets && assetState === RecordState.Archived && (
                  <>
                    <TablePageHeaderActionButton icon={<UndoIcon />} label="Restore Selected" onClick={() => setOpenRestoreConfirmation(true)} />
                    <TablePageHeaderActionButton
                      icon={<DeleteIcon className="text-default-red" />}
                      label="Delete Selected"
                      onClick={() => setOpenDeleteConfirmation(true)}
                    />
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </TablePageHeader>

      <ScrollArea type="always" viewportRef={setScrollRoot} className="relative flex flex-1 flex-col">
        <ScrollBar orientation="horizontal" />
        <AssetsTable
          data={flattenedData}
          units={units ?? []}
          loading={(isFetchingAssets && !isFetchingNextAssetsPage) || isLoadingPageConfig}
          notFoundDisplay={
            debouncedSearch.length > 0 ? (
              <NoAssetsFoundFromSearchDisplay />
            ) : filters && filters.length > 0 ? (
              <NoAssetsFoundFromFiltersDisplay
                openAssetDrawer={() => setOpenAssetDrawer(true)}
                removeColumnFilters={() => setModuleFilters(MODULE.Assets, [])}
              />
            ) : (
              <NoAssetsCreatedDisplay openAssetDrawer={() => setOpenAssetDrawer(true)} openBulkImportDialog={() => setOpenBulkImport(true)} />
            )
          }
          rowSelection={selectedRows}
          onRowSelectionChange={setSelectedRows}
          columnFilters={filters || []}
          onColumnFiltersChange={(filters) => setModuleFilters(MODULE.Assets, filters)}
          sorting={sorting}
          grouping={grouping}
          columnOrder={columnOrder}
          columnVisibility={columnVisibility}
          onSortingChange={(newSorting) =>
            updatePageConfig({
              table: { sorting: newSorting },
            })
          }
        />
        {hasNextAssetsPage && (
          <Button ref={sentinelRef} onClick={loadMore} disabled={!canLoadMore} className="mx-auto w-full text-center" variant="ghost">
            {isFetchingNextAssetsPage ? 'Loading more...' : 'Load Newer'}
          </Button>
        )}
      </ScrollArea>

      <ArchiveConfirmationDialog
        open={openArchiveConfirmation}
        onOpenChange={setOpenArchiveConfirmation}
        callback={handleArchiveAssets}
        loading={isUpdatingAssetState}
        title="Archive assets?"
        description="Are you sure you want to archive these assets? You can restore them later from the archived tab."
      />
      <RestoreConfirmationDialog
        open={openRestoreConfirmation}
        onOpenChange={setOpenRestoreConfirmation}
        callback={handleRestoreAssets}
        loading={isUpdatingAssetState}
        title="Restore assets?"
        description="Are you sure you want to restore these assets? They will be moved back to your active assets."
      />
      <DeleteConfirmationDialog
        open={openDeleteConfirmation}
        onOpenChange={setOpenDeleteConfirmation}
        callback={handleDeleteAssets}
        loading={isUpdatingAssetState}
        title="Delete assets?"
        description="This action is permanent and cannot be undone. Are you sure you want to delete these assets?"
      />
      <BulkUpdateAssetCategoryDialog
        open={openBulkUpdateAssetCategoryDialog}
        onOpenChange={setOpenBulkUpdateAssetCategoryDialog}
        callback={handleBulkUpdateAssetCategory}
        loading={isUpdatingAssetCategory}
      />
      <BulkUpdateAssetLocationDialog
        open={openBulkUpdateAssetLocationDialog}
        onOpenChange={setOpenBulkUpdateAssetLocationDialog}
        callback={handleBulkUpdateAssetLocation}
        loading={isUpdatingAssetLocation}
      />
      <BulkUpdateAssetTagsDialog
        open={openBulkUpdateAssetTagsDialog}
        onOpenChange={setOpenBulkUpdateAssetTagsDialog}
        callback={handleBulkUpdateAssetTags}
        loading={isUpdatingAssetTags}
      />
      {units && (
        <ColumnsDialog<AssetDTO>
          open={openColumnsDialog}
          defaultSectionLabel="Asset"
          onOpenChange={setOpenColumnsDialog}
          columns={createAssetTableColumns({ units: units })}
          columnOrder={columnOrder}
          onColumnOrderChange={(columnOrder) =>
            updatePageConfig({
              table: { columnOrder: columnOrder as Array<{ id: AssetColumnId; active: boolean }> },
            })
          }
          disabled={isLoadingPageConfig || isLoadingUnits}
          noTrigger
        />
      )}

      <QRPrintDocument type="assets" contentRef={printQrRef} qrs={selectedAssetsQRCodes} />
    </div>
  );
}

export default AssetsPage;
